import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Category, JournalEntry, LedgerAccount } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { Modal } from "../../shared/ui/Modal";
import { RowMenu } from "../../shared/ui/RowMenu";
import { Select } from "../../shared/ui/Select";
import { setTransactionCategory } from "../categories/api";
import { amountValue, localDateTimeValue, money, parseAmount, payeeName } from "../../shared/lib/format";
import { TransactionPlanningDialog } from "../planning/TransactionPlanningDialog";
import { refundTransaction, reverseTransaction } from "./api";
import { EmptyState } from "../../shared/ui/EmptyState";

interface ActivityPanelProps {
  bookID: string;
  entries: JournalEntry[];
  accounts: LedgerAccount[];
  onNotify: (message: string) => void;
  categories?: Category[];
  // Overview preview: a fixed number of read-only rows and a link out to the
  // journal, instead of the working list with its actions and pager.
  preview?: number;
}

export function ActivityPanel({ bookID, entries, accounts, onNotify, categories = [], preview }: ActivityPanelProps) {
  const queryClient = useQueryClient();
  const relabel = useMutation({
    mutationFn: ({ transactionID, categoryID }: { transactionID: string; categoryID: string }) => setTransactionCategory(bookID, transactionID, categoryID),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      onNotify("Category updated.");
    },
  });
  const accountByID = new Map(accounts.map((account) => [account.id, account]));
  const categoryByID = new Map(categories.map((category) => [category.id, category.name]));
  // A correction that was itself undone leaves its original standing again,
  // so only live corrections mark a transaction as corrected.
  const corrections = new Map(entries.flatMap((entry) => entry.transaction.references
    ?.filter((reference) => reference.kind === "reversal_of")
    .map((reference) => [entry.transaction.id, reference.transactionId] as const) ?? []));
  const undone = new Set([...corrections.keys()].filter((id) => corrections.has(id) && [...corrections.entries()].some(([undoID, target]) => target === id && undoID !== id)));
  const reversed = new Set([...corrections.entries()].filter(([id]) => !undone.has(id)).map(([, target]) => target));
  const [refunding, setRefunding] = useState("");
  // The transaction whose planning owner is being chosen, by id.
  const [planning, setPlanning] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundRefused, setRefundRefused] = useState("");
  const reversal = useMutation({
    mutationFn: (transactionID: string) => reverseTransaction(bookID, transactionID),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await queryClient.invalidateQueries({ queryKey: ["transactions"] });
      await queryClient.invalidateQueries({ queryKey: ["statement"] });
      onNotify("Transaction corrected. History was preserved.");
    },
  });

  const refund = useMutation({
    mutationFn: ({ entry, amount }: { entry: JournalEntry; amount: string }) => {
      const asset = entry.postings[0];
      const economic = entry.postings.find((posting) => !accountByID.has(posting.accountId) && posting.side === "debit") ?? entry.postings[0];
      if (!asset) throw new Error("This transaction has no asset account to refund into.");
      return refundTransaction(bookID, entry.transaction.id, {
        assetAccountId: asset.accountId, amount, currency: economic.amount.currency,
        effectiveAt: new Date(localDateTimeValue()).toISOString(),
        description: `Refund of ${entry.transaction.description || "transaction"}`,
      });
    },
    onSuccess: async () => {
      setRefunding("");
      setRefundAmount("");
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await queryClient.invalidateQueries({ queryKey: ["transactions"] });
      await queryClient.invalidateQueries({ queryKey: ["statement"] });
      onNotify("Refund recorded. It nets the original expense down instead of counting as income.");
    },
  });

  function requestReversal(entry: JournalEntry) {
    const isCorrection = entry.transaction.references?.some((reference) => reference.kind === "reversal_of") ?? false;
    const question = isCorrection
      ? "Undo this correction? The original transaction counts again and all three entries stay in the audit trail."
      : "Reverse this transaction? Its effect is cancelled, both entries stay in the audit trail, and you can undo this afterwards.";
    if (window.confirm(question)) reversal.mutate(entry.transaction.id);
  }

  const rows = preview ? entries.slice(0, preview) : entries;

  return (
    <section className="panel activity-panel" aria-labelledby="activity-title">
      <div className="panel-heading">
        <div><p className="eyebrow">Journal</p><h2 id="activity-title">{preview ? "Latest entries" : "Entries"}</h2></div>
        {preview ? <Link className="secondary-button" to="/journal">Open the journal</Link> : null}
      </div>
      <div className="transaction-list">
        {rows.map((entry) => {
    const assetPostings = entry.postings;
          const incoming = assetPostings.find((posting) => posting.side === "debit");
          const outgoing = assetPostings.find((posting) => posting.side === "credit");
          const isTransfer = Boolean(incoming && outgoing);
          const first = (isTransfer ? incoming : assetPostings[0]) ?? entry.postings[0];
          const sourceName = outgoing ? accountByID.get(outgoing.accountId)?.name : undefined;
          const destinationName = incoming ? accountByID.get(incoming.accountId)?.name : undefined;
          const names = isTransfer
            ? `${sourceName ?? "Account"} → ${destinationName ?? "Account"}`
            : [...new Set(assetPostings.map((posting) => accountByID.get(posting.accountId)?.name).filter(Boolean))].join(", ");
          const isCorrection = entry.transaction.references?.some((reference) => reference.kind === "reversal_of") ?? false;
          const increase = !isTransfer && first?.side === "debit";
          const rowKind = isTransfer ? "transfer" : (increase ? "income" : "expense");
          const description = entry.transaction.description || "Transaction";
          // The row is scanned for who was paid, not for how the acquirer
          // phrased it; the envelope stays reachable as the tooltip.
          const payee = payeeName(description);
          const when = new Intl.DateTimeFormat("en", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(entry.transaction.effectiveAt));
          const categoryName = entry.transaction.categoryId ? categoryByID.get(entry.transaction.categoryId) : undefined;
          const meta = [names || "Journal", when].filter(Boolean).join(" · ");
          const isReversed = reversed.has(entry.transaction.id);
          const isUndone = isCorrection && undone.has(entry.transaction.id);
          return (
            <article className={`transaction-row ${rowKind}`} key={entry.transaction.id}>
              <span className="transaction-icon" aria-hidden="true">{isCorrection ? "↶" : (isTransfer ? "↔" : (increase ? "↙" : "↗"))}</span>
              <span className="transaction-copy">
                <strong title={description}>{payee}</strong>
                <span title={meta}>{meta}
                  {entry.reporting && entry.reporting.currency !== first?.amount.currency ? ` · ${money(entry.reporting.minor, entry.reporting.currency)}` : ""}
                </span>
              </span>
              {isTransfer ? null : <span className={`row-tag ${categoryName ? "" : "untagged"}`}>{categoryName ?? "Uncategorized"}</span>}
              {isTransfer ? null : (
                <span className={`row-tag planning ${entry.planningAssignment ? "" : "untagged"}`}
                  title={entry.planningAssignment
                    ? `Planning: ${entry.planningAssignment.type === "group" ? "Group" : "Item"} — ${entry.planningAssignment.name}`
                    : "Planning: not assigned"}>
                  {entry.planningAssignment
                    ? `${entry.planningAssignment.type === "group" ? "Group" : "Item"} — ${entry.planningAssignment.name}`
                    : "Not assigned"}
                </span>
              )}
              <span className="transaction-actions">
                {first ? <span className="transaction-amount">{money(isTransfer ? first.amount.minor : (increase ? first.amount.minor : -first.amount.minor), first.amount.currency, !isTransfer)}</span> : null}
                {isReversed ? <span className="audit-label">Reversed</span> : isUndone ? <span className="audit-label">Undone</span> : null}
              </span>
              {preview || (isReversed && isUndone) ? null : (
                <RowMenu label={`Actions for ${payee}`}>
                  {!isTransfer && categories.length > 0 ? (
                    <div className="row-menu-field">
                      <span className="field-label">Category</span>
                      <Select
                        tone="compact" label={`Category for ${description}`} disabled={relabel.isPending}
                        value={entry.transaction.categoryId ?? ""}
                        onChange={(categoryID) => relabel.mutate({ transactionID: entry.transaction.id, categoryID })}
                        options={[{ value: "", label: "Uncategorized" }, ...categories.map((category) => ({ value: category.id, label: category.name }))]}
                      />
                    </div>
                  ) : null}
                  {!isReversed && !isCorrection && rowKind === "expense" ? (
                    <button className="row-menu-item" type="button" onClick={() => {
                      setRefunding(refunding === entry.transaction.id ? "" : entry.transaction.id);
                      setRefundAmount(first ? amountValue(first.amount.minor, first.amount.currency) : "");
                    }}>Refund part of this…</button>
                  ) : null}
                  {isTransfer ? null : (
                    <button className="row-menu-item" type="button" onClick={() => setPlanning(entry.transaction.id)}>
                      {entry.planningAssignment ? "Change the planning target…" : "Assign to a plan or group…"}
                    </button>
                  )}
                  {!isReversed && !isUndone ? (
                    <button className="row-menu-item danger" type="button" onClick={() => requestReversal(entry)} disabled={reversal.isPending}>
                      {isCorrection ? "Undo this correction" : "Reverse this transaction"}
                    </button>
                  ) : null}
                </RowMenu>
              )}
              {planning === entry.transaction.id ? (
                <TransactionPlanningDialog
                  open onClose={() => setPlanning("")} bookID={bookID} entry={entry} onNotify={onNotify}
                />
              ) : null}
              <Modal
                open={refunding === entry.transaction.id} onClose={() => setRefunding("")}
                title="Refund part of this expense" subtitle={description}
                footer={<>
                  <button className="secondary-button" type="button" onClick={() => setRefunding("")}>Cancel</button>
                  <button className="primary-button compact" type="submit" form={`refund-${entry.transaction.id}`} disabled={refund.isPending}>
                    <span>{refund.isPending ? "Recording…" : "Record refund"}</span><span aria-hidden="true">→</span>
                  </button>
                </>}
              >
                <form id={`refund-${entry.transaction.id}`} className="stacked-form" aria-label="Refund this expense"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const parsed = parseAmount(refundAmount);
                    if (parsed === null) {
                      setRefundRefused("That is not an amount. Digits, and at most three decimals.");
                      return;
                    }
                    setRefundRefused("");
                    refund.mutate({ entry, amount: parsed });
                  }}>
                  <label>Amount returned<span className="amount-input"><input value={refundAmount} onChange={(event) => setRefundAmount(event.target.value)} inputMode="decimal" autoFocus required /><span>{first?.amount.currency}</span></span></label>
                  <p className="panel-note">Part of this expense came back. Spending nets down by this amount; it is not recorded as income.</p>
                  {refundRefused ? <p className="form-error" role="alert">{refundRefused}</p> : null}
                  {refund.error ? <p className="form-error" role="alert">{humanError(refund.error)}</p> : null}
                </form>
              </Modal>
            </article>
          );
        })}
      </div>
      {entries.length === 0 ? <EmptyState>Nothing was recorded in this period. Change the period above, or record the first operation.</EmptyState> : null}
      {reversal.error ? <p className="form-error" role="alert">{humanError(reversal.error)}</p> : null}
      {refund.error ? <p className="form-error" role="alert">{humanError(refund.error)}</p> : null}
      {relabel.error ? <p className="form-error" role="alert">{humanError(relabel.error)}</p> : null}
    </section>
  );
}
